import React from "react";
import { ChevronRight, Copy, ArrowLeft } from "lucide-react";

const MAIN_CODE = "217C68";

const examCardBase =
    "group relative bg-white border border-[#E2E8F0] rounded-xl p-8 sm:p-10 cursor-pointer transition-all duration-300 flex flex-col items-start text-left h-full";
const examCardHover =
    "hover:-translate-y-1 hover:shadow-[0_12px_40px_-12px_rgba(27,42,71,0.18)] hover:border-[#1B2A47]";

const ExamCard = ({ exam, onSelect }) => {
    return (
        <button
            type="button"
            onClick={() => onSelect(exam.id)}
            data-testid={`exam-card-${exam.id}`}
            className={`${examCardBase} ${examCardHover} focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1B2A47] focus-visible:ring-offset-2`}
        >
            <span className="inline-block w-10 h-1 rounded-full bg-[#D32F2F]" />
            <h3 className="mt-5 text-xl sm:text-[22px] font-semibold leading-snug text-[#0A1128] font-[Outfit]">
                {exam.name}
            </h3>
            <div className="mt-auto pt-8 w-full flex items-center justify-between text-[#1B2A47]">
                <span className="text-sm font-medium">Select exam</span>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#F1F5F9] group-hover:bg-[#1B2A47] group-hover:text-white transition-colors">
                    <ChevronRight className="w-4 h-4" />
                </span>
            </div>
        </button>
    );
};

export const ExamSelector = ({
    exams,
    selectedExam,
    onSelect,
    onReset,
    onCopy,
}) => {
    if (selectedExam) {
        return (
            <div
                data-testid="selected-exam-state"
                className="zoom-fade max-w-2xl mx-auto bg-white border border-[#E2E8F0] rounded-2xl p-8 sm:p-12 text-center shadow-[0_20px_60px_-20px_rgba(27,42,71,0.15)]"
            >
                <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#64748B]">
                        Chosen LanguageCert exam
                    </span>
                    <button
                        type="button"
                        onClick={onReset}
                        data-testid="choose-another-exam-link"
                        className="inline-flex items-center gap-1 text-sm text-[#475569] hover:text-[#1B2A47] underline underline-offset-4 transition-colors"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        Choose another exam
                    </button>
                </div>

                <h3
                    data-testid="selected-exam-name"
                    className="mt-4 text-2xl sm:text-3xl font-semibold text-[#0A1128] font-[Outfit]"
                >
                    {selectedExam.name}
                </h3>

                <div className="mt-10">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#64748B] mb-3">
                        Your LanguageCert code
                    </p>
                    <div
                        data-testid="code-box"
                        className="inline-block min-w-[260px] sm:min-w-[340px] bg-[#F8F9FA] border border-dashed border-[#CBD5E1] rounded-xl py-6 px-8"
                    >
                        <span
                            data-testid="exam-code-value"
                            className="font-mono-code text-4xl sm:text-5xl font-bold tracking-[0.25em] text-[#0F172A] uppercase"
                        >
                            {MAIN_CODE}
                        </span>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={onCopy}
                    data-testid="copy-languagecert-code-button"
                    className="mt-10 inline-flex items-center justify-center gap-2 bg-[#1B2A47] hover:bg-[#0F172A] text-white px-8 py-3.5 rounded-md font-medium text-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1B2A47] focus-visible:ring-offset-2 active:scale-[0.98]"
                >
                    <Copy className="w-4 h-4" />
                    Copy LanguageCert code
                </button>
            </div>
        );
    }

    return (
        <div
            data-testid="exam-cards-grid"
            className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6"
        >
            {exams.map((exam, i) => (
                <div
                    key={exam.id}
                    className="fade-up"
                    style={{ animationDelay: `${i * 80}ms` }}
                >
                    <ExamCard exam={exam} onSelect={onSelect} />
                </div>
            ))}
        </div>
    );
};

export default ExamSelector;
